import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import StructuredData from '@/components/base/StructuredData';
import { getCategoryBySlug, getComicsByCategory, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const comics = getComicsByCategory(slug || '');

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl font-bold text-foreground-50 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <StructuredData type="Organization" />
      <StructuredData type="WebSite" />
      <StructuredData
        type="WebPage"
        data={{
          name: `${category.name}韩漫 - 韩漫屋`,
          description: `${category.description}韩漫屋提供${category.name}类免费韩漫在线阅读，共${category.count}部精选作品。`,
        }}
      />
      <StructuredData type="BreadcrumbList" />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full min-h-[50vh] flex items-center overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, oklch(0.06 0.002 275) 0%, oklch(0.10 0.015 25) 60%, oklch(0.14 0.04 40) 100%)',
              }}
            ></div>

            <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-12">
              <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16">
                <div className="lg:w-[60%] space-y-5">
                  <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
                    <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
                    <i className="ri-arrow-right-s-line text-foreground-600"></i>
                    <span className="text-primary-400">{category.name}</span>
                  </nav>

                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-500/15 text-primary-400 text-xs font-medium rounded-full border border-primary-500/20">
                    <i className="ri-book-open-line text-sm"></i>
                    共 {category.count} 部作品
                  </span>

                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 leading-tight">
                    {category.name}
                    <span className="block text-xl md:text-2xl font-normal text-foreground-300 mt-2">韩漫分类</span>
                  </h1>

                  <p className="text-sm md:text-base text-foreground-300 max-w-lg leading-relaxed">
                    {category.description}
                  </p>
                </div>

                <div className="lg:w-[40%] flex items-start justify-center lg:justify-end mt-4 lg:mt-0">
                  <div className="relative w-full max-w-sm">
                    <div className="absolute -inset-1 bg-accent-500/10 rounded-2xl blur-2xl"></div>
                    <div className="relative rounded-2xl overflow-hidden border border-background-200/30 aspect-[16/10]">
                      <img
                        src={category.image}
                        alt={`${category.name}韩漫`}
                        title={`${category.name} - 韩漫屋分类`}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h2 className="text-xl font-bold text-foreground-100">
                  全部作品 <span className="text-foreground-500 text-sm font-normal">({comics.length}部)</span>
                </h2>

                <div className="flex flex-wrap items-center gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-950'
                          : 'bg-background-100 text-foreground-400 border border-background-200/30 hover:border-primary-500/30 hover:text-foreground-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
                data-product-shop=""
              >
                {comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group relative rounded-2xl overflow-hidden border border-background-200/30 transition-all duration-300 hover:border-primary-500/20"
                  >
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - ${category.name}韩漫`}
                        title={`${comic.title} - 韩漫屋`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/20 to-transparent"></div>

                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] font-medium bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded-full border border-primary-500/10">
                          {comic.status}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-background-950/60 backdrop-blur-sm px-2 py-1 rounded-full">
                        <i className="ri-star-fill text-[10px] text-accent-500"></i>
                        <span className="text-[10px] text-foreground-200 font-medium">{comic.rating}</span>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-foreground-50 font-semibold text-sm mb-0.5">{comic.title}</p>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-foreground-500">{comic.author}</span>
                          <span className="text-foreground-700">·</span>
                          <span className="text-[10px] text-foreground-500">{comic.totalViews.toLocaleString()}人气</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {comics.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-foreground-400">该分类暂无漫画作品</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}